import recipeTranslationService from './recipeTranslationService';
import personalRecipeDetails from './personalRecipeDetails';
import unifiedRecipeService from './unifiedRecipeService';
import { auth } from './firebaseConfig';
import { PlannedMeal } from '../redux/mealPlanSlice';
import { Recipe } from '../types/recipe';
import { trackEvent } from './analyticsService';
import { displayLanguageOf } from '../utils/recipeLanguage';

export interface TranslatedMealUpdate {
  recipeId: string;
  translatedTitle: string;
  /**
   * The full translated recipe when one was available: title, ingredients and
   * instructions. Callers should write this whole object back onto the meal so
   * downstream consumers — most importantly grocery list generation, which
   * reads `extendedIngredients[].original` — see translated text too.
   *
   * Undefined when only a title translation could be obtained.
   */
  translatedRecipe?: Recipe;
}

/**
 * Fired when a background translation completes and produces a full recipe that
 * was NOT available at the time `translateMealPlanRecipes` returned.
 *
 * The caller dispatches this the same way it dispatches the synchronous
 * updates. That is what resolves the grocery list's pending skeleton without a
 * reload: the dispatch mutates `meal.recipe`, `useGroceryListSync` recomputes
 * `generateWeekSnapshot`, the hash moves because `ingredientsTranslated`
 * flipped, and the debounced rebuild picks up the translated names.
 */
export type LateTranslationHandler = (update: TranslatedMealUpdate) => void;

/** At most this many API translations per invocation. Overflow is left to the nightly cron. */
export const MAX_BACKGROUND_TRANSLATIONS = 8;
/** At most this many in flight at once, so a language switch never floods the API. */
export const BACKGROUND_TRANSLATION_CONCURRENCY = 3;


/**
 * Translate meal plan recipes to the target language.
 *
 * Prefers a stored full translation (title + ingredients + instructions) so the
 * grocery list ends up in one language. Falls back to a title-only translation
 * when no full translation has been generated for that recipe yet.
 *
 * Meals already in the target language or with unknown language are skipped.
 */
// Re-exported for the existing callers and tests that import it from here.
// The definition moved to src/utils/recipeLanguage so the grocery list can use
// it without pulling Firebase in through this module.
export { displayLanguageOf } from '../utils/recipeLanguage';

/**
 * Put a recipe back into its own language.
 *
 * Translation overwrites the recipe in place, so there is no original to hand
 * back — it is re-read from source. Only the fields translation touches are
 * restored, so meal-level overrides such as servings survive.
 *
 * Two sources, in cost order: the bundled catalog first (a local, cached read),
 * then the shared/saved corpus in Firestore. The catalog used to be the only
 * one, and that quietly became wrong — it holds 481 recipes while the app
 * serves ~1373, so a plan generated from Firestore could not be put back into
 * its own language at all. Switching a French app to English and back left
 * every Firestore-only recipe stuck in English, with one warning per recipe and
 * nothing on screen to explain it.
 *
 * Returns null when neither source knows the recipe, because there is then
 * genuinely nothing to restore from.
 */
async function restoreOriginal(recipe: Recipe, recipeId: string): Promise<Recipe | null> {
  const original =
    (await personalRecipeDetails.getRecipeDetails(recipeId)) ??
    (await unifiedRecipeService.getRecipeById(recipeId, auth?.currentUser?.uid));
  if (!original) return null;

  return {
    ...recipe,
    title: original.title,
    // Must list every field translation touches. Anything omitted here stays in
    // the translated language after a restore — that is how a French recipe
    // switched back to French kept an English description.
    summary: original.summary,
    extendedIngredients: original.extendedIngredients,
    instructions: original.instructions,
    ingredientGroups: original.ingredientGroups,
    instructionSections: original.instructionSections,
    dishTypes: original.dishTypes,
    cuisines: original.cuisines,
    diets: original.diets,
    isTranslated: false,
    // Restored text is the recipe's own language, so nothing is translated any
    // more. Clearing this also makes the snapshot hash move on a restore.
    ingredientsTranslated: false,
    translatedFrom: undefined,
  };
}

export async function translateMealPlanRecipes(
  meals: PlannedMeal[],
  targetLanguage: 'en' | 'fr',
  /**
   * Optional. When supplied, every meal that could only be given a title-only
   * translation is queued for a real one in the background, and this is called
   * with the result as each lands.
   *
   * Without it the behaviour is exactly as before — which is the bug: the plan
   * reads correctly while the shopping list stays in the source language,
   * silently and forever, because nothing else ever generates that translation
   * except opening the recipe detail by hand.
   */
  onLateTranslation?: LateTranslationHandler,
): Promise<TranslatedMealUpdate[]> {
  if (!recipeTranslationService.isAvailable()) {
    console.warn('Translation service not available, skipping meal plan translation');
    return [];
  }

  // Filter on what the recipe currently reads as, not what it was written in.
  const mealsToTranslate = meals.filter(meal => {
    if (!meal.recipe || meal.recipeId === 'free-text') return false;
    const shown = displayLanguageOf(meal.recipe);
    if (shown === 'unknown' || shown === targetLanguage) return false;
    return true;
  });

  if (mealsToTranslate.length === 0) return [];

  console.log(`🌐 Translating ${mealsToTranslate.length} recipes to ${targetLanguage}...`);

  // Meals that could only be given a title. Collected during the pass and
  // filled in the background afterwards.
  const titleOnlyMisses: PlannedMeal[] = [];

  const results = await Promise.allSettled(
    mealsToTranslate.map(async (meal) => {
      const recipe = meal.recipe!;

      // Going back to the recipe's own language is a restore, not a
      // translation — no stored translation exists in that direction.
      if (targetLanguage === recipe.originalLanguage) {
        const restored = await restoreOriginal(recipe, meal.recipeId);
        if (!restored) {
          throw new Error(
            `No catalog source to restore "${recipe.title}" to ${targetLanguage}`,
          );
        }
        return {
          recipeId: meal.recipeId,
          translatedTitle: restored.title,
          translatedRecipe: restored,
        };
      }

      // Stored-only: never fires a translation API call per planned meal.
      const full = await recipeTranslationService.translateRecipeFromStore(
        recipe,
        targetLanguage
      );
      if (full) {
        return {
          recipeId: meal.recipeId,
          translatedTitle: full.title,
          translatedRecipe: full,
        };
      }

      // No full translation stored yet. Fall back to the title so the plan at
      // least reads in the right language, and remember the miss so it can be
      // filled in the background below rather than staying wrong forever.
      //
      // The catalogue's own translated title comes first (#736). It is written
      // by the server, costs nothing to read, and a guest — who cannot reach the
      // translate endpoint — has it too. Looking elsewhere first let a broken
      // stored translation ("Fish Tacos", as the French) replace the good
      // "Tacos de poisson" on the plan.
      const catalogueTitle = recipe.translatedTitles?.[targetLanguage]?.trim();
      const translatedTitle = catalogueTitle || await recipeTranslationService.translateTitle(
        meal.recipeId,
        recipe.title,
        recipe.originalLanguage,
        targetLanguage
      );
      titleOnlyMisses.push(meal);
      return { recipeId: meal.recipeId, translatedTitle };
    })
  );

  const updates: TranslatedMealUpdate[] = [];
  results.forEach((result: PromiseSettledResult<TranslatedMealUpdate>, index: number) => {
    if (result.status === 'fulfilled') {
      updates.push(result.value);
    } else {
      console.warn(`Failed to translate recipe "${mealsToTranslate[index].recipe?.title}":`, result.reason);
    }
  });

  const withIngredients = updates.filter(u => u.translatedRecipe).length;
  console.log(
    `Translated ${updates.length}/${mealsToTranslate.length} recipes ` +
    `(${withIngredients} with ingredients, ${updates.length - withIngredients} title-only)`
  );

  if (onLateTranslation && titleOnlyMisses.length > 0) {
    // Deliberately NOT awaited. A language switch must not block on the
    // network, and the caller already has everything it needs to render.
    void fillTitleOnlyMisses(titleOnlyMisses, targetLanguage, onLateTranslation);
  }

  return updates;
}

/**
 * Buy the translations the store did not have.
 *
 * This is the half that makes the corpus self-healing. The translate endpoint
 * persists each result to `recipeTranslations/{id}` (#711), which is world-readable, so the
 * first user to plan a given recipe pays for it once and every user after that
 * gets it from the store for free.
 *
 * Bounded on purpose. The original design refused to call the API from here at
 * all — "switching language must not fire one Gemini call per planned meal" —
 * and that instinct was right; it was the silent giving-up that was wrong. So:
 * at most MAX_BACKGROUND_TRANSLATIONS per invocation, at most
 * BACKGROUND_TRANSLATION_CONCURRENCY at a time, no retries, and anything past
 * the cap is left to the nightly cron sweep rather than queued.
 */
async function fillTitleOnlyMisses(
  misses: PlannedMeal[],
  targetLanguage: 'en' | 'fr',
  onLateTranslation: LateTranslationHandler,
): Promise<void> {
  const queue = misses.slice(0, MAX_BACKGROUND_TRANSLATIONS);
  const overflowed = misses.length - queue.length;
  if (overflowed > 0) {
    // Never truncate silently: a shorter list that looks complete is how this
    // class of bug hides in the first place.
    console.warn(
      `${overflowed} recipe(s) over the per-switch translation cap; ` +
      `leaving them to the nightly sweep`,
    );
  }

  // Report the miss once per recipe, not once per time it appears in the week.
  const reported = new Set<string>();
  const report = (meal: PlannedMeal, outcome: 'queued' | 'filled' | 'failed') => {
    const key = `${meal.recipeId}:${targetLanguage}:${outcome}`;
    if (reported.has(key)) return;
    reported.add(key);
    trackEvent('recipe_translation_missing', {
      recipeId: meal.recipeId,
      sourceLanguage: meal.recipe?.originalLanguage ?? 'unknown',
      targetLanguage,
      surface: 'meal-plan',
      outcome,
      overflowed,
    });
  };

  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < queue.length) {
      const meal = queue[cursor++];
      const recipe = meal.recipe;
      if (!recipe) continue;
      // A stub — a meal whose ingredients and steps have not been loaded yet —
      // is not the recipe. Translating it produced a shared row with a title
      // and nothing else, which then hid the real translation from everyone
      // (#736). Rehydration fills the meal in; the next pass translates that.
      if (!recipe.extendedIngredients?.length && !recipe.instructions?.length) continue;
      report(meal, 'queued');
      try {
        const full = await recipeTranslationService.translateRecipe(recipe, targetLanguage);
        // translateRecipe resolves even when it could not translate (it returns
        // the input recipe unchanged), so check rather than assume.
        if (!full?.ingredientsTranslated) {
          report(meal, 'failed');
          continue;
        }
        onLateTranslation({
          recipeId: meal.recipeId,
          translatedTitle: full.title,
          translatedRecipe: full,
        });
        report(meal, 'filled');
      } catch (error) {
        // Leave the item showing its skeleton. That is the honest state, and
        // the nightly sweep will fill it. Retrying inline would just burn the
        // cap on a recipe that is probably failing for a structural reason.
        console.warn(`🌐 Background translation failed for "${recipe.title}":`, error);
        report(meal, 'failed');
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(BACKGROUND_TRANSLATION_CONCURRENCY, queue.length) }, worker),
  );
}
